import React, { useMemo } from "react";
import { Badge } from "@shared/ui/badge";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { useParams } from "react-router-dom";
import { useGetPlanDetailByTab, useGetParameter } from "../../../hooks";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { BadgeList } from "@shared/components/BadgeList";

const safeRender = (val: any, defaultVal = "NA", currency = ""): string => {
  if (val === null || val === undefined || val === "") return defaultVal;
  if (
    typeof val === "string" ||
    typeof val === "number" ||
    typeof val === "boolean"
  ) {
    return String(val);
  }
  if (typeof val === "object") {
    if ("fixed" in val || "variable" in val || "unit" in val) {
      const parts: string[] = [];
      if (val.fixed !== undefined && val.fixed !== null && val.fixed !== "") {
        const fixedStr =
          typeof val.fixed === "number" && currency
            ? `${currency} ${val.fixed}`
            : String(val.fixed);
        parts.push(`Fixed: ${fixedStr}`);
      }
      if (
        val.variable !== undefined &&
        val.variable !== null &&
        val.variable !== ""
      ) {
        const varStr =
          typeof val.variable === "number" && currency
            ? `${currency} ${val.variable}`
            : String(val.variable);
        const unitStr = val.unit ? ` / ${val.unit}` : "";
        parts.push(`Variable: ${varStr}${unitStr}`);
      }
      if (parts.length > 0) return parts.join(" | ");
      if (val.unit) return String(val.unit);
    }
    if (Array.isArray(val)) {
      return (
        val
          .map((v) => safeRender(v, "", currency))
          .filter(Boolean)
          .join(", ") || defaultVal
      );
    }
    const vals = Object.values(val).filter(
      (v) => v !== null && v !== undefined && typeof v !== "object",
    );
    if (vals.length > 0) return vals.join(" ");
  }
  return defaultVal;
};

const parseStringList = (val: any): string[] => {
  if (!val) return [];
  const list: string[] = [];
  const rawArray = Array.isArray(val) ? val.flat() : [val];
  rawArray.forEach((item: any) => {
    if (typeof item === "string" && item.trim()) {
      item.split(",").forEach((s) => {
        const trimmed = s.trim();
        if (trimmed) list.push(trimmed);
      });
    } else if (item !== null && item !== undefined && item !== "") {
      const rendered = safeRender(item, "");
      if (rendered) list.push(rendered);
    }
  });
  return list;
};

export const ChargesTaxes = () => {
  const { id } = useParams();
  const currency = useCurrency();
  const { data: planDetail } = useGetPlanDetailByTab(id, "charges");
  const { data: globalParamData } = useGetParameter();

  const parameterOptions = useMemo(() => {
    const raw = Array.isArray(globalParamData)
      ? globalParamData
      : globalParamData?.result || globalParamData?.data || [];
    return Array.isArray(raw) ? raw : [];
  }, [globalParamData]);

  const resolveParamName = (rule: any) => {
    if (!rule || typeof rule !== "object") return "—";
    const rawName =
      rule.parameterName || rule.parameter_name || rule.parameterNameDisplay;
    if (
      rawName &&
      typeof rawName !== "object" &&
      String(rawName).toLowerCase() !== "common" &&
      String(rawName) !== "NA"
    )
      return safeRender(rawName, "—");
    const pId = rule.parameterId || rule.parameter_id || rule.parameter;
    if (!pId) return "—";
    const found = parameterOptions.find(
      (p: any) =>
        String(p.id) === String(pId) || String(p.parameterId) === String(pId),
    );
    if (found) {
      const name = found.name || found.parameterName || String(pId);
      return safeRender(name, String(pId));
    }
    return safeRender(pId, "—");
  };

  const rawResult = planDetail?.result || {};

  const linkedChargesRaw =
    rawResult.linkedCharges || rawResult.linked_charges || [];
  const fixedChargesRaw =
    rawResult.fixedCharges || rawResult.fixed_charges || [];
  const surchargesRaw = rawResult.surcharges || [];
  const taxesRaw = rawResult.taxes || [];

  const linkedChargesData = linkedChargesRaw.map((item: any, idx: number) => ({
    id: idx.toString(),
    name: item.name || "NA",
    linkedService: item.linkedService || item.linked_service || "NA",
    usage:
      item.usage !== undefined && item.usage !== null && item.usage !== ""
        ? item.usage
        : "NA",
    appliesTo: item.appliesTo || item.applies_to || "NA",
    rate: item.chargeRate ?? item.charge_rate ?? item.rate ?? "NA",
  }));

  const COL_WIDTH = {
    NAME: "w-[20%]",
    SERVICE: "w-[18%]",
    PARAM: "w-[18%]",
    TYPE: "w-[14%]",
    APPLIES: "w-[18%]",
    AMOUNT: "w-[12%]",
  };

  const linkedChargesColumns: TableColumn<any>[] = [
    {
      key: "name",
      header: "Name",
      width: "w-[25%]",
      render: (val, row) => (
        <span className="font-medium text-gray-900">
          {safeRender(row.name, "NA")}
        </span>
      ),
    },
    {
      key: "linkedService",
      header: "Linked Service",
      width: "w-[25%]",
      render: (val, row) => {
        const ls = safeRender(row.linkedService, "NA");
        return ls && ls !== "NA" ? (
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-yellow-400"></span>
            {ls}
          </div>
        ) : (
          <span className="text-gray-400">NA</span>
        );
      },
    },
    {
      key: "usage",
      header: "Usage (%)",
      width: "w-[25%]",
      render: (val, row) => (
        <span className="text-gray-600 font-medium">
          {safeRender(row.usage, "NA")}
        </span>
      ),
    },
    {
      key: "rate",
      header: "Amount",
      width: "w-[25%]",
      render: (val, row) => (
        <span className="font-medium text-gray-900">
          {safeRender(row.rate, "NA", currency)}
        </span>
      ),
    },
  ];

  // Fixed Charges Columns
  const fixedChargesCommonColumns: TableColumn<any>[] = [
    {
      key: "name",
      header: "Name",
      width: "w-[40%]",
      render: (val: any, row: any) => (
        <span className="font-medium text-gray-900">
          {safeRender(row.name, "NA")}
        </span>
      ),
    },
    {
      key: "includeIn",
      header: "Included In",
      width: "w-[30%]",
      render: (val: any, row: any) => {
        const inc = safeRender(row.includeIn || row.include_in || val, "NA");
        return inc && inc !== "NA" ? (
          <span className="capitalize text-gray-700">{inc} bill</span>
        ) : (
          <span className="text-gray-400">NA</span>
        );
      },
    },
    {
      key: "amount",
      header: "Amount",
      width: "w-[30%]",
      render: (val: any, row: any) => (
        <Badge
          variant="outline"
          className="bg-green-50 text-green-600 border-green-200 hover:bg-green-50 font-medium rounded-full px-3"
        >
          {safeRender(row.amount, "NA", currency)}
        </Badge>
      ),
    },
  ];

  const fixedChargesParamWiseColumns: TableColumn<any>[] = [
    {
      key: "name",
      header: "Name",
      width: COL_WIDTH.NAME,
      render: (val: any, row: any) => (
        <span className="font-medium text-gray-900">
          {safeRender(row.name, "NA")}
        </span>
      ),
    },
    {
      key: "utilityService",
      header: "Utility Service",
      width: COL_WIDTH.SERVICE,
      render: (val: any, row: any) => {
        const items = parseStringList(
          row.utilityServices || row.utility_services || row.utilityService,
        );
        return (
          <BadgeList
            items={items}
            count={2}
            badgeClassName="bg-cyan-50 text-cyan-700 border-cyan-200"
            fallback={<span className="text-gray-400 text-xs">All</span>}
          />
        );
      },
    },
    {
      key: "parameter",
      header: "Parameter Name",
      width: COL_WIDTH.PARAM,
      render: (val: any, row: any) => (
        <span className="font-medium text-gray-800">
          {resolveParamName(row)}
        </span>
      ),
    },
    {
      key: "includeIn",
      header: "Included In",
      width: COL_WIDTH.TYPE,
      render: (val: any, row: any) => {
        const inc = row.includeIn || row.include_in || val;
        return inc && inc !== "NA" ? (
          <span className="capitalize text-gray-700">{inc} bill</span>
        ) : (
          <span className="text-gray-400">NA</span>
        );
      },
    },
    {
      key: "appliesTo",
      header: "Applies To",
      width: COL_WIDTH.APPLIES,
      render: (val: any, row: any) => {
        const items = parseStringList(
          row.parameterValues || row.parameter_values || row.appliesTo,
        );
        return (
          <BadgeList
            items={items}
            count={2}
            badgeClassName="bg-purple-50 text-purple-700 border-purple-200"
            fallback={<span className="text-gray-400 text-xs">All</span>}
          />
        );
      },
    },
    {
      key: "amount",
      header: "Amount",
      width: COL_WIDTH.AMOUNT,
      render: (val: any, row: any) => (
        <Badge
          variant="outline"
          className="bg-green-50 text-green-600 border-green-200 hover:bg-green-50 font-medium rounded-full px-3"
        >
          {row.amount || "NA"}
        </Badge>
      ),
    },
  ];

  // Surcharges Columns
  const surchargesCommonColumns: TableColumn<any>[] = [
    {
      key: "name",
      header: "Name",
      width: "w-[40%]",
      render: (val: any, row: any) => (
        <span className="font-medium text-gray-900">
          {safeRender(row.name, "NA")}
        </span>
      ),
    },
    {
      key: "type",
      header: "Type",
      width: "w-[30%]",
      render: (val: any, row: any) => {
        const t = safeRender(row.type || val, "NA");
        return t && t !== "NA" ? (
          <Badge
            variant="secondary"
            className="bg-purple-100 text-purple-700 hover:bg-purple-100 font-normal rounded-full px-3 capitalize"
          >
            {t}
          </Badge>
        ) : (
          <span className="text-gray-400">NA</span>
        );
      },
    },
    {
      key: "rate",
      header: "Amount",
      width: "w-[30%]",
      render: (val: any, row: any) => (
        <Badge
          variant="outline"
          className="bg-green-50 text-green-600 border-green-200 font-medium rounded-full px-3"
        >
          {safeRender(row.rate || row.amount, "NA", currency)}
        </Badge>
      ),
    },
  ];

  const surchargesParamWiseColumns: TableColumn<any>[] = [
    {
      key: "name",
      header: "Name",
      width: COL_WIDTH.NAME,
      render: (val: any, row: any) => (
        <span className="font-medium text-gray-900">
          {safeRender(row.name, "NA")}
        </span>
      ),
    },
    {
      key: "utilityService",
      header: "Utility Service",
      width: COL_WIDTH.SERVICE,
      render: (val: any, row: any) => {
        const items = parseStringList(
          row.utilityServices || row.utility_services || row.utilityService,
        );
        return (
          <BadgeList
            items={items}
            count={2}
            badgeClassName="bg-cyan-50 text-cyan-700 border-cyan-200"
            fallback={<span className="text-gray-400 text-xs">All</span>}
          />
        );
      },
    },
    {
      key: "parameter",
      header: "Parameter",
      width: COL_WIDTH.PARAM,
      render: (val: any, row: any) => (
        <span className="font-medium text-gray-800">
          {resolveParamName(row)}
        </span>
      ),
    },
    {
      key: "type",
      header: "Type",
      width: COL_WIDTH.TYPE,
      render: (val: any, row: any) => {
        const t = safeRender(row.type || val, "NA");
        return t && t !== "NA" ? (
          <Badge
            variant="secondary"
            className="bg-purple-100 text-purple-700 hover:bg-purple-100 font-normal rounded-full px-3 capitalize"
          >
            {t}
          </Badge>
        ) : (
          <span className="text-gray-400">NA</span>
        );
      },
    },
    {
      key: "appliesTo",
      header: "Applies To",
      width: COL_WIDTH.APPLIES,
      render: (val: any, row: any) => {
        const items = parseStringList(
          row.parameterValues || row.parameter_values || row.appliesTo,
        );
        return (
          <BadgeList
            items={items}
            count={2}
            badgeClassName="bg-purple-50 text-purple-700 border-purple-200"
            fallback={<span className="text-gray-400 text-xs">All</span>}
          />
        );
      },
    },
    {
      key: "rate",
      header: "Amount",
      width: COL_WIDTH.AMOUNT,
      render: (val: any, row: any) => (
        <Badge
          variant="outline"
          className="bg-green-50 text-green-600 border-green-200 font-medium rounded-full px-3"
        >
          {safeRender(row.rate || row.amount, "NA", currency)}
        </Badge>
      ),
    },
  ];

  // Taxes Columns
  const taxesCommonColumns: TableColumn<any>[] = [
    {
      key: "name",
      header: "Name",
      width: "w-[40%]",
      render: (val: any, row: any) => (
        <span className="font-medium text-gray-900">
          {safeRender(row.name, "NA")}
        </span>
      ),
    },
    {
      key: "type",
      header: "Type",
      width: "w-[30%]",
      render: (val: any, row: any) => {
        const t = safeRender(row.type || val, "NA");
        return t && t !== "NA" ? (
          <Badge
            variant="secondary"
            className="bg-purple-100 text-purple-700 hover:bg-purple-100 font-normal rounded-full px-3 capitalize"
          >
            {t}
          </Badge>
        ) : (
          <span className="text-gray-400">NA</span>
        );
      },
    },
    {
      key: "rate",
      header: "Amount",
      width: "w-[30%]",
      render: (val: any, row: any) => {
        const type = String(row.type || "").toLowerCase();
        const isFlat =
          type === "flatrate" || type === "flat_rate" || type === "flat";
        const isPercentage = type === "percentage" || type === "percent";

        let targetVal: any = null;
        if (isFlat) {
          targetVal =
            row.amount !== undefined &&
            row.amount !== null &&
            row.amount !== "" &&
            Number(row.amount) !== 0
              ? row.amount
              : row.rate !== undefined && row.rate !== null && row.rate !== ""
                ? row.rate
                : row.amount;
        } else if (isPercentage) {
          targetVal =
            row.rate !== undefined &&
            row.rate !== null &&
            row.rate !== "" &&
            Number(row.rate) !== 0
              ? row.rate
              : row.amount !== undefined &&
                  row.amount !== null &&
                  row.amount !== ""
                ? row.amount
                : row.rate;
        } else {
          targetVal =
            row.amount && Number(row.amount) !== 0
              ? row.amount
              : (row.rate ?? row.amount);
        }

        let formatted = "NA";
        if (targetVal !== null && targetVal !== undefined && targetVal !== "") {
          if (typeof targetVal === "object") {
            formatted = safeRender(targetVal, "NA", currency);
          } else if (isPercentage) {
            formatted = `${targetVal}%`;
          } else {
            formatted = String(targetVal).includes(currency)
              ? String(targetVal)
              : `${currency} ${targetVal}`;
          }
        }
        return formatted !== "NA" ? (
          <Badge
            variant="outline"
            className="bg-green-50 text-green-600 border-green-200 font-medium rounded-full px-3"
          >
            {formatted}
          </Badge>
        ) : (
          <span className="text-gray-400">NA</span>
        );
      },
    },
  ];

  const taxesParamWiseColumns: TableColumn<any>[] = [
    {
      key: "name",
      header: "Name",
      width: COL_WIDTH.NAME,
      render: (val: any, row: any) => (
        <span className="font-medium text-gray-900">
          {safeRender(row.name, "NA")}
        </span>
      ),
    },
    {
      key: "utilityService",
      header: "Utility Service",
      width: COL_WIDTH.SERVICE,
      render: (val: any, row: any) => {
        const items = parseStringList(
          row.utilityServices || row.utility_services || row.utilityService,
        );
        return (
          <BadgeList
            items={items}
            count={2}
            badgeClassName="bg-cyan-50 text-cyan-700 border-cyan-200"
            fallback={<span className="text-gray-400 text-xs">All</span>}
          />
        );
      },
    },
    {
      key: "parameter",
      header: "Parameter",
      width: COL_WIDTH.PARAM,
      render: (val: any, row: any) => (
        <span className="font-medium text-gray-800">
          {resolveParamName(row)}
        </span>
      ),
    },
    {
      key: "type",
      header: "Type",
      width: COL_WIDTH.TYPE,
      render: (val: any, row: any) => {
        let t = safeRender(row.type || val, "NA");
        if (t === "flat_rate" || t === "flat") t = "flatRate";
        else if (t === "percent") t = "percentage";
        return t && t !== "NA" ? (
          <Badge
            variant="secondary"
            className="bg-purple-100 text-purple-700 hover:bg-purple-100 font-normal rounded-full px-3"
          >
            {t}
          </Badge>
        ) : (
          <span className="text-gray-400">NA</span>
        );
      },
    },
    {
      key: "appliesTo",
      header: "Applies To",
      width: COL_WIDTH.APPLIES,
      render: (val: any, row: any) => {
        const items = parseStringList(
          row.parameterValues || row.parameter_values || row.appliesTo,
        );
        return (
          <BadgeList
            items={items}
            count={2}
            badgeClassName="bg-purple-50 text-purple-700 border-purple-200"
            fallback={<span className="text-gray-400 text-xs">All</span>}
          />
        );
      },
    },
    {
      key: "rate",
      header: "Amount",
      width: COL_WIDTH.AMOUNT,
      render: (val: any, row: any) => {
        const type = String(row.type || "").toLowerCase();
        const isFlat =
          type === "flatrate" || type === "flat_rate" || type === "flat";
        const isPercentage = type === "percentage" || type === "percent";

        let targetVal: any = null;
        if (isFlat) {
          targetVal =
            row.amount !== undefined &&
            row.amount !== null &&
            row.amount !== "" &&
            Number(row.amount) !== 0
              ? row.amount
              : row.rate !== undefined && row.rate !== null && row.rate !== ""
                ? row.rate
                : row.amount;
        } else if (isPercentage) {
          targetVal =
            row.rate !== undefined &&
            row.rate !== null &&
            row.rate !== "" &&
            Number(row.rate) !== 0
              ? row.rate
              : row.amount !== undefined &&
                  row.amount !== null &&
                  row.amount !== ""
                ? row.amount
                : row.rate;
        } else {
          targetVal =
            row.amount && Number(row.amount) !== 0
              ? row.amount
              : (row.rate ?? row.amount);
        }

        let formatted = "NA";
        if (targetVal !== null && targetVal !== undefined && targetVal !== "") {
          if (typeof targetVal === "object") {
            formatted = safeRender(targetVal, "NA", currency);
          } else if (isPercentage) {
            formatted = `${targetVal}%`;
          } else {
            formatted = String(targetVal).includes(currency)
              ? String(targetVal)
              : `${currency} ${targetVal}`;
          }
        }
        return formatted !== "NA" ? (
          <Badge
            variant="outline"
            className="bg-green-50 text-green-600 border-green-200 font-medium rounded-full px-3"
          >
            {formatted}
          </Badge>
        ) : (
          <span className="text-gray-400">NA</span>
        );
      },
    },
  ];

  const getSectionMode = (rawData: any[]) => {
    if (!Array.isArray(rawData) || rawData.length === 0) return "common";
    const hasParamGroup = rawData.some(
      (item) =>
        Array.isArray(item.rules) &&
        item.parameter_name?.toLowerCase() !== "common" &&
        item.parameterName?.toLowerCase() !== "common" &&
        (item.parameter_id || item.parameterId),
    );
    const hasParamRule = rawData.some(
      (item) =>
        item.mode === "parameter-wise" ||
        item.mode === "parameter_wise" ||
        item.mode === "PARAMETER_WISE",
    );
    return hasParamGroup || hasParamRule ? "parameter-wise" : "common";
  };

  const renderSection = (
    rawData: any[],
    commonColumns: TableColumn<any>[],
    paramWiseColumns: TableColumn<any>[],
    emptyMessage: string,
  ) => {
    if (!Array.isArray(rawData) || rawData.length === 0) {
      return <div className="text-sm text-gray-500 px-2">{emptyMessage}</div>;
    }

    const isGrouped = rawData.some((item) => Array.isArray(item.rules));

    if (isGrouped) {
      return (
        <div className="space-y-4">
          {rawData.map((group: any, groupIndex: number) => {
            const rules = group.rules || [];
            const paramName = safeRender(
              group.parameter_name || group.parameterName,
              "Common",
            );
            const isCommonGroup =
              (!group.parameter_id &&
                !group.parameterId &&
                paramName.toLowerCase() === "common") ||
              rules.every(
                (r: any) =>
                  !r.mode || r.mode === "common" || r.mode === "COMMON",
              );
            const columnsToUse = isCommonGroup
              ? commonColumns
              : paramWiseColumns;

            if (isCommonGroup) {
              return (
                <div
                  key={groupIndex}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <GenericTable
                    data={rules}
                    columns={columnsToUse}
                    rowKey="id"
                  />
                </div>
              );
            }

            return (
              <div
                key={groupIndex}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center gap-2">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Parameter:
                  </span>
                  <span className="text-sm font-semibold text-gray-900">
                    {paramName}
                  </span>
                  <Badge
                    variant="outline"
                    className="bg-white text-gray-600 border-gray-200 rounded-full px-2 text-xs"
                  >
                    {rules.length} {rules.length === 1 ? "rule" : "rules"}
                  </Badge>
                </div>
                <GenericTable data={rules} columns={columnsToUse} rowKey="id" />
              </div>
            );
          })}
        </div>
      );
    }

    const isCommon = rawData.every(
      (item) => !item.mode || item.mode === "common" || item.mode === "COMMON",
    );
    const columnsToUse = isCommon ? commonColumns : paramWiseColumns;

    return (
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <GenericTable data={rawData} columns={columnsToUse} rowKey="id" />
      </div>
    );
  };

  const fixedMode = getSectionMode(fixedChargesRaw);
  const surchargesMode = getSectionMode(surchargesRaw);
  const taxesMode = getSectionMode(taxesRaw);

  const linkedCount = linkedChargesData.length;

  const fixedCount = useMemo(() => {
    if (!Array.isArray(fixedChargesRaw)) return 0;
    return fixedChargesRaw.reduce((acc: number, item: any) => {
      if (Array.isArray(item.rules)) return acc + item.rules.length;
      return acc + 1;
    }, 0);
  }, [fixedChargesRaw]);

  const surchargesCount = useMemo(() => {
    if (!Array.isArray(surchargesRaw)) return 0;
    return surchargesRaw.reduce((acc: number, item: any) => {
      if (Array.isArray(item.rules)) return acc + item.rules.length;
      return acc + 1;
    }, 0);
  }, [surchargesRaw]);

  const totalTaxesCount = useMemo(() => {
    if (!Array.isArray(taxesRaw)) return 0;
    return taxesRaw.reduce((acc: number, item: any) => {
      if (Array.isArray(item.rules)) return acc + item.rules.length;
      return acc + 1;
    }, 0);
  }, [taxesRaw]);

  return (
    <div className="space-y-6">
      {/* Linked Charges Section */}
      <div>
        <div className="mb-1 flex items-center gap-2">
          <h3 className="text-base font-semibold text-gray-900">
            Linked Charges
          </h3>
          {linkedCount > 0 && (
            <Badge
              variant="outline"
              className="bg-yellow-50 text-yellow-700 border-yellow-200 rounded-full px-2 py-0.5 text-xs"
            >
              {linkedCount} {linkedCount === 1 ? "rule" : "rules"}
            </Badge>
          )}
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Charges computed as a percentage of usage from another utility
          service.
        </p>
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <GenericTable
            data={linkedChargesData}
            columns={linkedChargesColumns}
            rowKey="id"
          />
        </div>
      </div>

      {/* Fixed Charges Section */}
      <div>
        <div className="mb-1 flex items-center gap-2">
          <h3 className="text-base font-semibold text-gray-900">
            Fixed Charges
          </h3>
          <Badge
            variant="outline"
            className="bg-purple-50 text-purple-700 border-purple-200 rounded-full px-2 py-0.5 text-xs capitalize"
          >
            {fixedMode}
          </Badge>
          {fixedCount > 0 && (
            <Badge
              variant="outline"
              className="bg-yellow-50 text-yellow-700 border-yellow-200 rounded-full px-2 py-0.5 text-xs"
            >
              {fixedCount} {fixedCount === 1 ? "rule" : "rules"}
            </Badge>
          )}
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Fixed fees applied periodically.
        </p>
        {renderSection(
          fixedChargesRaw.map((fc: any, i: number) => ({
            id: i.toString(),
            name: fc.name || "NA",
            utilityService:
              fc.utilityServices || fc.utility_services || fc.utilityService,
            includeIn: fc.includeIn || fc.include_in || "NA",
            appliesTo: fc.parameterValues || fc.parameter_values || fc.value,
            amount:
              fc.amount !== undefined && fc.amount !== null && fc.amount !== ""
                ? `${currency} ${fc.amount}`
                : "NA",
            mode: fc.mode,
            parameterName: fc.parameterName || fc.parameter_name,
            parameterId: fc.parameterId || fc.parameter_id || fc.parameter,
          })),
          fixedChargesCommonColumns,
          fixedChargesParamWiseColumns,
          "No fixed charges configured",
        )}
      </div>

      {/* Surcharges Section */}
      <div>
        <div className="mb-1 flex items-center gap-2">
          <h3 className="text-base font-semibold text-gray-900">Surcharges</h3>
          <Badge
            variant="outline"
            className="bg-purple-50 text-purple-700 border-purple-200 rounded-full px-2 py-0.5 text-xs capitalize"
          >
            {surchargesMode}
          </Badge>
          {surchargesCount > 0 && (
            <Badge
              variant="outline"
              className="bg-yellow-50 text-yellow-700 border-yellow-200 rounded-full px-2 py-0.5 text-xs"
            >
              {surchargesCount} {surchargesCount === 1 ? "rule" : "rules"}
            </Badge>
          )}
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Additional fees applied on top of usage or fixed charges.
        </p>
        {renderSection(
          surchargesRaw.map((sc: any, i: number) => {
            const rateVal =
              sc.rate !== undefined && sc.rate !== null && sc.rate !== ""
                ? sc.rate
                : sc.amount !== undefined &&
                    sc.amount !== null &&
                    sc.amount !== ""
                  ? sc.amount
                  : null;
            return {
              id: i.toString(),
              name: sc.name || "NA",
              utilityService:
                sc.utilityServices || sc.utility_services || sc.utilityService,
              type: sc.type || "NA",
              appliesTo: sc.parameterValues || sc.parameter_values || sc.value,
              rate:
                rateVal !== null
                  ? sc.type === "percentage"
                    ? `${rateVal}%`
                    : `${currency} ${rateVal}`
                  : "NA",
              mode: sc.mode,
              parameterName: sc.parameterName || sc.parameter_name,
              parameterId: sc.parameterId || sc.parameter_id || sc.parameter,
            };
          }),
          surchargesCommonColumns,
          surchargesParamWiseColumns,
          "No surcharges configured",
        )}
      </div>

      {/* Taxes Section */}
      <div>
        <div className="mb-1 flex items-center gap-2">
          <h3 className="text-base font-semibold text-gray-900">Taxes</h3>
          <Badge
            variant="outline"
            className="bg-purple-50 text-purple-700 border-purple-200 rounded-full px-2 py-0.5 text-xs capitalize"
          >
            {taxesMode}
          </Badge>
          {totalTaxesCount > 0 && (
            <Badge
              variant="outline"
              className="bg-yellow-50 text-yellow-700 border-yellow-200 rounded-full px-2 py-0.5 text-xs"
            >
              {totalTaxesCount} {totalTaxesCount === 1 ? "rule" : "rules"}
            </Badge>
          )}
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Each tax rule fires when the consumer's parameters matches at billing
          time... rates can vary by customer class, district, account type, or
          any other parameter.
        </p>
        {renderSection(
          taxesRaw,
          taxesCommonColumns,
          taxesParamWiseColumns,
          "No taxes configured",
        )}
      </div>
    </div>
  );
};

export default ChargesTaxes;
